// Typed calls to otto-app's public routes, validated with the contract
// schemas in @otto/api. Every call returns a result the page must handle: a
// failure is never turned into a fake success (house rule: loud or nothing).
import {
  ApiErrorBody,
  CheckinConfigResponse,
  CheckinResponse,
  FunnelEvent,
  HumanRequest,
  LiveTokenResponse,
  ResumableResponse,
  ResumeInfoResponse,
  StatusResponse,
  publicRoutes,
  type CheckinRequest,
  type ResumeHereRequest,
  type ResumeRequest,
} from "@otto/api";
import type { z } from "zod";

export type ApiFailure = {
  ok: false;
  /** HTTP status, or 0 when the request never got an answer. */
  status: number;
  code: string;
  message: string;
  retryAfterSeconds?: number;
  redirectUrl?: string;
  issues?: string[];
};

export type ApiResult<T> = { ok: true; data: T } | ApiFailure;

export type CheckinConfig = z.infer<typeof CheckinConfigResponse>;

type Fetch = typeof fetch;

function fetcher(custom?: Fetch): Fetch {
  if (custom) return custom;
  if (typeof fetch !== "function") throw new Error("fetch is not available in this browser");
  return fetch.bind(globalThis);
}

async function call<T>(
  input: { method: "GET" | "POST"; path: string; body?: unknown },
  parse: (json: unknown) => ApiResult<T>,
  customFetch?: Fetch,
): Promise<ApiResult<T>> {
  let response: Response;
  try {
    response = await fetcher(customFetch)(input.path, {
      method: input.method,
      headers:
        input.body === undefined
          ? { accept: "application/json" }
          : { "content-type": "application/json", accept: "application/json" },
      body: input.body === undefined ? undefined : JSON.stringify(input.body),
      credentials: "same-origin",
    });
  } catch {
    return {
      ok: false,
      status: 0,
      code: "network",
      message: "We could not reach Otto's Office. Check your connection and try again.",
    };
  }
  let json: unknown = null;
  try {
    json = await response.json();
  } catch {
    json = null;
  }
  if (!response.ok) {
    const error = ApiErrorBody.safeParse(json);
    if (error.success) {
      const e = error.data.error;
      return {
        ok: false,
        status: response.status,
        code: e.code,
        message: e.message,
        ...(e.retry_after_seconds !== undefined ? { retryAfterSeconds: e.retry_after_seconds } : {}),
        ...(e.redirect_url !== undefined ? { redirectUrl: e.redirect_url } : {}),
        ...(e.issues !== undefined ? { issues: e.issues } : {}),
      };
    }
    return {
      ok: false,
      status: response.status,
      code: response.status === 404 ? "not_found" : "bad_response",
      message: "Something went wrong on our side.",
    };
  }
  return parse(json);
}

function badShape(): ApiFailure {
  return { ok: false, status: 200, code: "bad_response", message: "Something went wrong on our side." };
}

export function getCheckinConfig(customFetch?: Fetch): Promise<ApiResult<CheckinConfig>> {
  return call(
    { method: "GET", path: publicRoutes.checkinConfig.path },
    (json) => {
      const parsed = CheckinConfigResponse.safeParse(json);
      return parsed.success ? { ok: true, data: parsed.data } : badShape();
    },
    customFetch,
  );
}

export function postCheckin(
  request: CheckinRequest,
  customFetch?: Fetch,
): Promise<ApiResult<z.infer<typeof CheckinResponse>>> {
  return call(
    { method: "POST", path: publicRoutes.checkin.path, body: request },
    (json) => {
      const parsed = CheckinResponse.safeParse(json);
      return parsed.success ? { ok: true, data: parsed.data } : badShape();
    },
    customFetch,
  );
}

/** Who a link to finish an unfinished consult is for (CR-015). The token travels in the body only. */
export function postResumeInfo(token: string, customFetch?: Fetch): Promise<ApiResult<ResumeInfoResponse>> {
  return call(
    { method: "POST", path: publicRoutes.resumeInfo.path, body: { token } },
    (json) => {
      const parsed = ResumeInfoResponse.safeParse(json);
      return parsed.success ? { ok: true, data: parsed.data } : badShape();
    },
    customFetch,
  );
}

/** Start the consult that continues the unfinished one; answers like a check-in. */
export function postResume(request: ResumeRequest, customFetch?: Fetch): Promise<ApiResult<z.infer<typeof CheckinResponse>>> {
  return call(
    { method: "POST", path: publicRoutes.resume.path, body: request },
    (json) => {
      const parsed = CheckinResponse.safeParse(json);
      return parsed.success ? { ok: true, data: parsed.data } : badShape();
    },
    customFetch,
  );
}

/** Whether this browser's last consult can be picked back up (CR-016; the HttpOnly cookie proves it). */
export function getResumable(customFetch?: Fetch): Promise<ApiResult<ResumableResponse>> {
  return call(
    { method: "GET", path: publicRoutes.consultResumable.path },
    (json) => {
      const parsed = ResumableResponse.safeParse(json);
      return parsed.success ? { ok: true, data: parsed.data } : badShape();
    },
    customFetch,
  );
}

/** Pick this browser's unfinished consult back up (CR-016); answers like a check-in. */
export function postResumeHere(request: ResumeHereRequest, customFetch?: Fetch): Promise<ApiResult<z.infer<typeof CheckinResponse>>> {
  return call(
    { method: "POST", path: publicRoutes.consultResumeHere.path, body: request },
    (json) => {
      const parsed = CheckinResponse.safeParse(json);
      return parsed.success ? { ok: true, data: parsed.data } : badShape();
    },
    customFetch,
  );
}

export function getConsultStatus(
  consultId: string,
  customFetch?: Fetch,
): Promise<ApiResult<z.infer<typeof StatusResponse>>> {
  return call(
    { method: "GET", path: publicRoutes.consultStatus.path.replace(":id", encodeURIComponent(consultId)) },
    (json) => {
      const parsed = StatusResponse.safeParse(json);
      return parsed.success ? { ok: true, data: parsed.data } : badShape();
    },
    customFetch,
  );
}

export function postRating(
  consultId: string,
  body: { score: number; comment?: string },
  customFetch?: Fetch,
): Promise<ApiResult<{ ok: true }>> {
  return call(
    { method: "POST", path: publicRoutes.consultRating.path.replace(":id", encodeURIComponent(consultId)), body },
    () => ({ ok: true, data: { ok: true } }),
    customFetch,
  );
}

/**
 * A fresh live token for this consult (the HttpOnly consult cookie proves the
 * browser): for a new tab, or the thank-you page's Resume after the tab that
 * checked in was closed.
 */
export function getLiveToken(
  consultId: string,
  customFetch?: Fetch,
): Promise<ApiResult<z.infer<typeof LiveTokenResponse>>> {
  return call(
    { method: "GET", path: publicRoutes.consultLiveToken.path.replace(":id", encodeURIComponent(consultId)) },
    (json) => {
      const parsed = LiveTokenResponse.safeParse(json);
      return parsed.success ? { ok: true, data: parsed.data } : badShape();
    },
    customFetch,
  );
}

/** "Prefer not to talk? A person will reach out" (design 7.7). A failure shows the callback link instead. */
export function postHumanRequest(body: HumanRequest, customFetch?: Fetch): Promise<ApiResult<{ ok: true }>> {
  const valid = HumanRequest.safeParse(body);
  if (!valid.success) {
    return Promise.resolve({
      ok: false,
      status: 0,
      code: "invalid_input",
      message: "Please check the details above.",
      issues: valid.error.issues.map((i) => i.path.join(".")),
    });
  }
  return call(
    { method: "POST", path: publicRoutes.humanRequest.path, body: valid.data },
    () => ({ ok: true, data: { ok: true } }),
    customFetch,
  );
}

/**
 * Counts-only funnel step (POST /api/funnel). Fire and forget by design: a
 * lost count must never block or slow an owner, so this is a documented no-op
 * on failure, not a hidden one. sendBeacon survives page unloads.
 */
export function sendFunnel(event: FunnelEvent): void {
  const parsed = FunnelEvent.safeParse(event);
  if (!parsed.success) return;
  const body = JSON.stringify(parsed.data);
  try {
    if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
      navigator.sendBeacon(publicRoutes.funnel.path, new Blob([body], { type: "application/json" }));
      return;
    }
    if (typeof fetch === "function") {
      void fetch(publicRoutes.funnel.path, {
        method: "POST",
        body,
        headers: { "content-type": "application/json" },
        keepalive: true,
      }).catch(() => undefined);
    }
  } catch {
    // Documented no-op: funnel counts are best effort (see above).
  }
}
